import { useEffect, useState, useCallback, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Typography,
} from "antd";
import apiHelpers from "./data";
import { STORES, hashForLabel } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";

const BUCKET_KEY_REGEX = /^[a-z0-9_-]+$/;

const toSlug = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_");

const STORE_OPTIONS = STORES.map((s) => ({
  value: s.hash,
  label: `${s.label} — ${s.name} (${s.hash})`,
}));

const emptyForm = (defaultStoreHash) => ({
  store_hash: defaultStoreHash || hashForLabel("UAT") || "",
  bucket_key: "",
  display_name: "",
  sort_order: 0,
});

const normalizeIncoming = (data) => ({
  store_hash: data.storeHash || "",
  bucket_key: data.bucketKey || "",
  display_name: data.displayName || "",
  sort_order: Number(data.sortOrder) || 0,
});

const diffPayload = (current, original) => {
  if (!original) return current;
  const patch = {};
  Object.keys(current).forEach((k) => {
    if (current[k] !== original[k]) patch[k] = current[k];
  });
  return patch;
};

const BucketFormModal = ({
  open,
  mode,
  bucketId,
  defaultStoreHash,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);
  const [userEditedKey, setUserEditedKey] = useState(false);
  const [existingSortOrders, setExistingSortOrders] = useState([]);
  const [formDirty, setFormDirty] = useState(false);
  const initialValuesRef = useRef(null);

  const isCreate = mode === "create";
  const title = isCreate ? "Add Bucket" : "Edit Bucket";

  const checkDirty = useCallback(() => {
    const current = JSON.stringify(form.getFieldsValue(true));
    setFormDirty(current !== initialValuesRef.current);
  }, [form]);

  const fetchNextSortOrder = async (storeHash) => {
    if (!storeHash) return 1;
    try {
      const { items } = await apiHelpers.fetchBuckets({
        store_hash: storeHash,
        sort_by: "sort_order",
        sort_order: "DESC",
        page: 1,
        limit: 100,
      });
      const orders = items.map((b) => Number(b.sortOrder ?? b.sort_order ?? 0));
      setExistingSortOrders(orders);
      const last = orders[0] ?? 0;
      return last + 1;
    } catch {
      return 1;
    }
  };

  useEffect(() => {
    if (!open) return;

    if (isCreate || !bucketId) {
      const initial = emptyForm(defaultStoreHash);
      form.resetFields();
      form.setFieldsValue(initial);
      setOriginal(null);
      setUserEditedKey(false);
      initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
      setFormDirty(false);
      fetchNextSortOrder(defaultStoreHash).then((next) => {
        form.setFieldValue("sort_order", next);
      });
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiHelpers.fetchBucketById(bucketId);
        if (cancelled) return;
        const normalized = normalizeIncoming(data);
        form.resetFields();
        form.setFieldsValue(normalized);
        setOriginal(normalized);
        initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
        setFormDirty(false);
      } catch (err) {
        const mapped = apiHelpers.mapError(err);
        triggerNotification({
          type: "error",
          title: "Failed to load bucket",
          message: mapped.message,
        });
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bucketId, mode]);

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const normalized = {
      store_hash: String(values.store_hash || "").trim(),
      bucket_key: String(values.bucket_key || "").trim(),
      display_name: String(values.display_name || "").trim(),
      sort_order: Number(values.sort_order) || 0,
    };

    setSaving(true);
    try {
      let saved;
      if (isCreate) {
        saved = await apiHelpers.createBucket(normalized);
      } else {
        const patch = diffPayload(normalized, original);
        if (Object.keys(patch).length === 0) {
          triggerNotification({
            type: "info",
            title: "No changes",
            message: "Nothing to save.",
          });
          onClose?.();
          return;
        }
        saved = await apiHelpers.updateBucket(bucketId, patch);
      }
      triggerNotification({
        type: "success",
        title: isCreate ? "Bucket created" : "Bucket updated",
        message: saved?.displayName || saved?.bucketKey || "Saved successfully.",
      });
      onSaved?.(saved);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      if (mapped.code === "VALIDATION_ERROR" && Array.isArray(mapped.details)) {
        const fieldMap = {
          store_hash: "store_hash",
          bucket_key: "bucket_key",
          display_name: "display_name",
          sort_order: "sort_order",
        };
        const errors = mapped.details
          .filter((d) => d && fieldMap[d.path])
          .map((d) => ({ name: fieldMap[d.path], errors: [d.message] }));
        if (errors.length) form.setFields(errors);
      } else if (mapped.code === "DUPLICATE_KEY") {
        form.setFields([
          { name: "bucket_key", errors: ["This bucket key already exists for this store."] },
        ]);
      }
      triggerNotification({
        type: "error",
        title: isCreate ? "Create failed" : "Update failed",
        message: mapped.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      onOk={handleOk}
      okText="Save"
      confirmLoading={saving}
      okButtonProps={{ disabled: !isCreate && !formDirty }}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onValuesChange={checkDirty}>
          <Form.Item
            name="store_hash"
            label="Store"
            rules={[
              { required: true, message: "Store is required" },
              { max: 64, message: "Max 64 chars" },
            ]}
          >
            <Select
              showSearch
              options={STORE_OPTIONS}
              placeholder="Select a store"
              optionFilterProp="label"
              onChange={(hash) => {
                if (isCreate) {
                  fetchNextSortOrder(hash).then((next) => {
                    form.setFieldValue("sort_order", next);
                  });
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="Display name"
            rules={[
              { required: true, message: "Display name is required" },
              { max: 255, message: "Max 255 chars" },
              { pattern: /^[a-zA-Z0-9\s_\-.,!?'"''""&()/:;]+$/, message: "Special characters like @#%^*+=<>{}[]\\|~` are not allowed" },
              { pattern: /[a-zA-Z]/, message: "Must contain at least one letter. Only numbers not allowed." },
            ]}
          >
            <Input
              placeholder="e.g. Hero Carousel"
              onChange={(e) => {
                if (!userEditedKey) {
                  form.setFieldValue("bucket_key", toSlug(e.target.value));
                  form.validateFields(["bucket_key"]).catch(() => {});
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="bucket_key"
            label={
              <span>
                Bucket key{" "}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  (auto-generated · a-z, 0-9, '_', '-')
                </Typography.Text>
              </span>
            }
            rules={[
              { required: true, message: "Bucket key is required" },
              { max: 64, message: "Max 64 chars" },
              {
                pattern: BUCKET_KEY_REGEX,
                message: "Only lowercase letters, digits, '_' and '-'",
              },
            ]}
          >
            <Input
              placeholder="e.g. hero_carousel"
              autoFocus={isCreate}
              onChange={(e) => {
                setUserEditedKey(true);
                const cleaned = e.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9_-]/g, "");
                form.setFieldValue("bucket_key", cleaned);
              }}
            />
          </Form.Item>

          <Form.Item
            name="sort_order"
            label="Sort order"
            rules={[
              { type: "number", min: 0, message: "Must be 0 or greater", transform: (v) => Number(v) },
              {
                validator: (_, value) => {
                  if (value === null || value === undefined) return Promise.resolve();
                  const num = Number(value);
                  const originalOrder = original ? Number(original.sort_order) : null;
                  // Skip check if editing and sort order hasn't changed
                  if (!isCreate && originalOrder === num) return Promise.resolve();
                  if (existingSortOrders.includes(num)) {
                    return Promise.reject(`Sort order ${num} is already used by another bucket in this store.`);
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber min={0} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default BucketFormModal;
